import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { QrCode, Lock, Mail, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password, remember);
      toast.success(`Hoş geldin, ${u.name || u.email}`);
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full relative flex items-center justify-center overflow-hidden bg-[#0A0A0E]">
      <img
        src="https://images.unsplash.com/photo-1758073519996-6d3c63b4922c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHRlY2hub2xvZ3klMjBkYXJrfGVufDB8fHx8MTc4NDA5NzM0NHww&ixlib=rb-4.1.0&q=85"
        alt="qrpts"
        className="absolute inset-0 w-full h-full object-cover opacity-50"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0E]/70 via-[#0A0A0E]/40 to-[#0A0A0E]/90" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="rounded-2xl p-8 border border-white/10" style={{
          background: "rgba(20,20,26,0.72)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          boxShadow: "0 24px 48px rgba(0,0,0,0.4)",
        }}>
          <div className="flex flex-col items-center text-center mb-8">
            <img src="/logo/qrpts-logo.png" alt="QRPTS" className="w-32 h-32 object-contain drop-shadow-2xl" data-testid="login-logo" />
          </div>

          <h1 className="font-display text-3xl font-bold text-white tracking-tight mb-1 text-center">Hoş Geldiniz</h1>
          <p className="text-sm text-white/60 mb-8 text-center">Devam etmek için giriş yapın</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-white/80 text-xs uppercase tracking-wider">
                E-posta
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  id="email"
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@qrpts.com"
                  required
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-blue-500"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-white/80 text-xs uppercase tracking-wider">
                Şifre
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  id="password"
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                  data-testid="toggle-password"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm pt-1">
              <label className="flex items-center gap-2 text-white/70 cursor-pointer">
                <Checkbox
                  data-testid="remember-me"
                  checked={remember}
                  onCheckedChange={(v) => setRemember(!!v)}
                  className="border-white/30 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                />
                <span>Beni Hatırla</span>
              </label>
              <button
                type="button"
                data-testid="forgot-password"
                className="text-blue-400 hover:text-blue-300 text-sm"
                onClick={() => nav("/forgot-password")}
              >
                Şifremi Unuttum
              </button>
            </div>

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={busy}
              className="w-full mt-2 bg-blue-600 hover:bg-blue-500 text-white h-11 rounded-lg font-semibold tracking-tight"
            >
              {busy ? "Giriş yapılıyor..." : "Giriş Yap"}
            </Button>
          </form>

          <div className="mt-6 pt-5 border-t border-white/10 text-xs text-white/50">
            <div>Yöneticiler için özel hesap ile giriş yapın.</div>
            <div className="mt-1">Personel doğrudan QR + PIN ile giriş-çıkış yapar.</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
